import {
  BizNestClientError,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  Icompany,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile, IfilterProps,
  Isalary,
  Istaff,
  IstaffAnalytics,
  Isuccess,
  Iuserperm,
  TstaffRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { Company } from '../company/company.define';
import { CommerceUserBase } from './commerce-user-base.define';

/**
 * Represents a staff member in the commerce system, extending the base commerce user functionality.
 * This class provides methods for managing staff data, including CRUD operations, file uploads, and permissions.
 *
 * @extends {CommerceUserBase} Provides common commerce user functionality
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Staff} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const staff = await Staff.getAll();
 *
 * const staff = await Staff.filterAll({
 *
 * });
 *
 * const staff = await Staff.bulkDelete({
 *
 * });
 *
 * const staff = await Staff.getOne('resourceId');
 *
 * const staff = await Staff.remove();
 *
 * ```
 */
export class Staff extends CommerceUserBase {
  /**
   * Base URL for staff-related API endpoints.
   * @private @static @readonly
   */
  private static readonly baseUrl = '/staff';

  /** The type of employment (e.g., full-time, part-time, contract). @readonly */
  readonly employmentType: string;

  /** Salary information for the staff member. Optional. @readonly */
  readonly salary?: Isalary;

  /** Instance-specific base URL for staff API endpoints. @protected */
  protected override baseUrl = '/staff';

  /**
   * Creates a new Staff instance.
   * @param {Istaff} data - Staff data for initialization
   * @throws {BizNestClientError} When no data is provided
   * @example
   * const staff = new Staff({
   *   _id: '123',
   *   employmentType: 'full-time',
   *   // ...other fields
   * });
   */
  constructor(data: Istaff) {
    if (!data) {
      throw new BizNestClientError('No data provided');
    }
    super(data);
    this.employmentType = data.employmentType || '';
    this.salary = data.salary;
  }

  /**
   * Retrieves staff analytics based on filter criteria.
   * @static
   * @param {IfilterProps} filter - Filter criteria for analytics
   * @returns {Promise<IstaffAnalytics>} Staff analytics data
   * @example
   * const analytics = await Staff.analytics({
   *   dateRange: { start: '2023-01-01', end: '2023-12-31' }
   * });
   */
  static analytics(filter: IfilterProps): Promise<IstaffAnalytics> {
    const observer$ = CommerceClient.ehttp
      .makePost<IstaffAnalytics>(`${Staff.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves paginated list of staff members.
   * @static
   * @param {number} [offset=0] - Pagination offset
   * @param {number} [limit=20] - Items per page
   * @returns {Promise<{count: number, dataArr: Staff[]}>} Paginated staff list
   * @example
   * const { count, dataArr } = await Staff.getAll(0, 10);
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({ offset: offset.toString(), limit: limit.toString() });
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<Istaff>>(`${Staff.baseUrl}?${query}`);
    const { count, data } = await lastValueFrom(observer$);

    return { count, dataArr: data.map(d => new Staff(d)) };
  }

  /**
   * Filters staff members based on the provided filter properties.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving staff members.
   * @returns {Promise<{ count: number, dataArr: Staff[] }>} An object containing
   * the total count and an array of `Staff` instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<Istaff>>(`${Staff.baseUrl}/filter`, filter);
    const staffs = await lastValueFrom(observer$);

    return {
      count: staffs.count,
      dataArr: staffs.data.map(val => new Staff(val))
    };
  }

  /**
   * Fetches a single staff member based on the provided filter.
   *
   * @param {string} resourceId - The ID of the staff member to retrieve.
   * @returns {Promise<Staff>} The `Staff` instance corresponding to the provided filter.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<Istaff>(`${Staff.baseUrl}/${resourceId}`);
    const staff = await lastValueFrom(observer$);

    return new Staff(staff);
  }

  /**
   * Retrieves the current staff member's profile.
   *
   * @returns {Promise<Staff>} The `Staff` instance corresponding to the current staff member.
   */
  static async myProfiles(): Promise<{ staff: Staff; company: Company }[]> {
    const observer$ = CommerceClient.ehttp
      .makePost<{ staff: Istaff; company: Icompany }[]>(`${Staff.baseUrl}/my-profiles`, {});
    const staff = await lastValueFrom(observer$);

    return staff.map(val => ({
      staff: new Staff(val.staff),
      company: new Company(val.company)
    }));
  }

  /**
   * Adds a new staff member, optionally with associated files.
   *
   * @param {IStaffRequest<true>} payload - The staff data to add.
   * @returns {Promise<Staff>} The result of the addition operation.
   */
  static async add(payload: TstaffRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<Istaff>(`${Staff.baseUrl}`, payload);

    return new Staff(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple staff members based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for deleting multiple staff members.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the deletion operation.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${Staff.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  static validateInvitation(payload: { token: string }) {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(`${Staff.baseUrl}/validate-invitation`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Uploads multiple files to the staff member.
   *
   * @param {Ifile[]} files - The files to upload.
   * @returns {Promise<Istaff>} The result of the file upload operation.
   */
  uploadImages(files: Ifile[]) {
    return CommerceClient.ehttp
      .uploadFiles<Istaff>(files, `${Staff.baseUrl}/upload-images`, { _id: this._id });
  }

  /**
   * Uploads profilePicture associated with the current Staff instance.
   * @param file - The image to upload.
   * @returns An observable that emits a success object.
   */
  uploadProfilePicture(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Istaff>([file], `${Staff.baseUrl}/upload-profile-picture`, { _id: this._id });
  }

  /**
   * Uploads cover pic associated with the current Staff instance.
   * @param file - The image to upload.
   * @returns An observable that emits a success object.
   */
  uploadProfileCoverPicture(file: Ifile) {
    return CommerceClient.ehttp
      .uploadFiles<Istaff>([file], `${Staff.baseUrl}/upload-profile-cover-picture`, { _id: this._id });
  }

  async setImageAs(
    fileId: string,
    field: 'profilePicture' | 'profileCoverPicture'
  ) {
    const observer$ = CommerceClient.ehttp
      .makePost<Istaff>(`${Staff.baseUrl}/set-image-as`, { fileId, field });

    return new Staff(await lastValueFrom(observer$));
  }

  /**
   * Deletes images associated with the current user.
   *
   * @param {string[]} fileIds - The IDs of the files to delete.
   * @returns {Promise<Staff>} The result of the deletion operation.
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp
      .makePost<Istaff>(this.baseUrl + '/delete-images', { fileIds, _id: this._id });

    return new Staff(await lastValueFrom(observer$));
  }

  /**
   * Updates the current staff instance, optionally with associated files.
   *
   * @param {IStaffRequest} payload - The updated staff data.
   * @returns {Promise<Staff>} The result of the update operation.
   */
  async update(payload: TstaffRequest): Promise<Staff> {
    const observer$ = CommerceClient.ehttp
      .makePut<Istaff>(`${Staff.baseUrl}/${this._id}`, payload);

    return new Staff(await lastValueFrom(observer$));
  }

  /**
   * Removes the current staff instance.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the removal operation.
   */
  remove(): Promise<IdeleteOneResponse | null> {
    return lastValueFrom(CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Staff.baseUrl}/${this._id}`));
  }

  /**
   * Retrieves the current staff member's permission.
   *
   * @returns {Promise<Iuserperm>} The permission of the current staff member.
   */
  getPermission() {
    const observer$ = CommerceClient.ehttp
      .makeGet<Iuserperm>(`${Staff.baseUrl}/${this._id}/get-permission`);

    return lastValueFrom(observer$);
  }

  /**
   * Invites a new staff member.
   *
   * @param {Object} payload - The invitation data.
   * @param {string} payload.email - The email address of the staff member to invite.
   * @param {string} payload.phone - The phone number of the staff member to invite.
   * @param {string} payload.phoneCode - The phone code of the staff member to invite.
   * @param {string} payload.method - The method of invitation (e.g., 'email', 'sms').
   * @param {string} payload.staffId - The ID of the staff member to invite.
   * @returns {Promise<{ qrCodeUrl?: string }>} The result of the invitation operation.
   */
  invite(payload: {
    email: string; phone: string;
    phoneCode: string; method: string;
    staffId: string; }): Promise<{ qrCodeUrl?: string }> {
    const observer$ = CommerceClient.ehttp
      .makePost<{ qrCodeUrl?: string }>(`${Staff.baseUrl}/invite`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the permissions of the current staff member.
   *
   * @param {Iuserperm} payload - The permission data to update.
   * @returns {Promise<Iuserperm>} The result of the update operation.
   */
  updatePermission(payload: Iuserperm) {
    const observer$ = CommerceClient.ehttp
      .makePost<Iuserperm>(`${Staff.baseUrl}/update-permission`, {
        _id: this._id,
        permissions: payload
      });

    return lastValueFrom(observer$);
  }
}
